import type {ComponentProps, FC, ReactElement} from "react";
import {memo, useCallback, useLayoutEffect, useRef, useState} from "react";

import ImageRenderer from "../ImageRenderer";

import Overlay from "./Overlay";

type Props = {
    url: string;
    imageAlt: string;
    isOpen: boolean;
    loadingIcon?: ReactElement;
    imageRenderer?: ComponentProps<typeof ImageRenderer>["imageRenderer"];
    onClose?: () => void;
};

// specific for web
const ImagePreview: FC<Props> = ({isOpen, url, imageAlt, loadingIcon, imageRenderer, onClose}) => {
    const [containerRect, setContainerRect] = useState<{
        width: number;
        height: number;
    }>({
        width: 0,
        height: 0
    });
    const [containerNode, setContainerNode] = useState<HTMLElement | null>(null);

    const resizeObserverRef = useRef(
        new ResizeObserver(entires => {
            setContainerRect({...entires[0]?.contentRect});
        })
    );
    const containerRef = useCallback((node: HTMLElement | null) => {
        setContainerNode(node);
    }, []);

    useLayoutEffect(() => {
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = new ResizeObserver(entires => {
            if (entires[0]?.contentRect) {
                const {width, height} = entires[0].contentRect;
                setContainerRect({width, height});
            }
        });
    }, [setContainerRect]);

    useLayoutEffect(() => {
        resizeObserverRef.current?.disconnect();
        if (containerNode) {
            resizeObserverRef.current?.observe(containerNode);
        }
    }, [containerNode, resizeObserverRef]);

    return (
        <Overlay open={isOpen} onClose={onClose}>
            <div className="image-slides-view-port">
                <ul ref={containerRef} className="image-slides-gallery">
                    <li className="image-slides-blackboard">
                        <ImageRenderer
                            containerRect={containerRect}
                            imageAlt={imageAlt}
                            imageRenderer={imageRenderer}
                            loadingIcon={loadingIcon}
                            url={url}
                        />
                    </li>
                </ul>
            </div>
        </Overlay>
    );
};

export default memo(ImagePreview);
